// The home page for a first visit: this week's Flip, playable, with the
// questions over it. Only for someone who arrives signed out — signed in, / is
// the feed (AccountApp's HomeRoute).
//
// A plain visit to / opens on the landing first: the logo, a welcome, and
// Let's jam, over the room blurred behind. A deep link (`/?hub=…`) skips it.
//
// The modal asks how to jam (solo / together), then where to start, and
// shows you there in the same modal — the tutorial views: the hub's panels
// with no tabs, the answers as the trail. Sign-up waits for the action itself
// (Plant it, Start, Join), and the action resumes after it, on the same step:
// signing in (a new account or an existing one) never moves you. Log in sits
// in the corner for anyone who already has an account. A share of visitors
// gets the four answers on one screen instead (lib/onboarding-variant.ts).
// × (or Skip) drops you into the room behind — the Flip, or the seed you
// chose to remix right here — without playing it; the Seedbed mark brings the
// landing back (signed in by then, the feed).
//
// The modal is a stack of screens: Back pops (off the first, to the landing).
// The hub's tab rides in the address (`/?hub=discover`).
//
// The plan: uiux/10.19 Nati Rework/plan.md.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, ChevronLeft, ChevronRight, ExternalLink, Maximize2, X } from "lucide-react";
import type { FeedCard, Flip } from "@shared/api-types.js";
import { getSession } from "@web-shared/lib/account";
import { getAnonId } from "@web-shared/lib/analytics";
import { onboardingArm } from "@/lib/onboarding-variant";
import { json, webApi } from "@web-shared/lib/web-api";
import { track } from "@web-shared/lib/analytics";
import { cn } from "@web-shared/lib/utils";
import { audiotoolHubPath, profilePath, remixInAbletonUrl, roomPath, seedPath } from "@/lib/ctas";
import { AmbientGlow } from "@/components/AmbientGlow";
import { JoinGlyph, LiveGlyph, MultiplayerGlyph, RecordGlyph, SoloGlyph } from "@/components/glyphs";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import { useLiveJams } from "../live";
import { useStartJam } from "../hooks/useStartJam";
import { OtherStep } from "../components/DawPicker";
import {
  DiscoverPanel,
  HUB_TABS,
  isHubTab,
  LivePanel,
  RecentPanel,
  RemixChooser,
  type CardActions,
  type Daw,
  type HubSeed,
  type HubTab,
} from "../components/HomeHub";
import { HubExpand } from "../components/HubCarousel";
import { UploadFlow } from "./UploadFilesPage";
import { LocalRoom, type StageHandle } from "./RoomPage";

/** The question screens ("four" is the A/B's one-screen version). */
type Step = "jam_style" | "solo" | "multiplayer" | "start_jam" | "four";

/** A first-visit answer, shown in the modal: Get inspired (to remix, or to
 * jam on), or the live jams to join. */
type Tour = "inspired" | "jam_inspired" | "join";

/** One screen of the modal. */
type Screen =
  | { kind: "step"; step: Step }
  | { kind: "hub"; tab: HubTab }
  | { kind: "tour"; tour: Tour }
  | { kind: "upload"; thenJam: boolean }
  | { kind: "remix"; seed: { id: string; title: string } }
  | { kind: "ableton"; seed: { id: string; title: string } }
  | { kind: "other"; seed: { id: string; title: string } };

const STEP_CRUMB: Record<Step, string> = {
  jam_style: "",
  solo: "Jam solo",
  multiplayer: "Jam together",
  start_jam: "Start a jam",
  four: "",
};

function crumbOf(s: Screen): string {
  switch (s.kind) {
    case "step":
      return STEP_CRUMB[s.step];
    case "hub":
      return HUB_TABS.find((t) => t.id === s.tab)?.label ?? "";
    case "tour":
      return s.tour === "join" ? "Join a jam" : "Get inspired";
    case "upload":
      return "Plant a seed";
    case "remix":
      return "Remix";
    case "ableton":
      return "Ableton";
    case "other":
      return "Other DAW";
  }
}

/** The Flip's tempo, for the tiles to move in time with (styles/globals.css). */
const BeatContext = createContext<number>(120);

/** The questions, counted for onboarding analytics. */
const QUESTION: ReadonlySet<Step> = new Set(["jam_style", "solo", "multiplayer", "start_jam", "four"]);

export function HomePage() {
  const session = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [arm] = useState(() => onboardingArm(getAnonId(), params.get("ob")));
  const root: Screen = { kind: "step", step: arm === "tiles" ? "four" : "jam_style" };
  const linkedTab = params.get("hub");
  const [stack, setStack] = useState<Screen[]>(() => (isHubTab(linkedTab) ? [root, { kind: "hub", tab: linkedTab }] : [root]));
  /** The landing, on a plain visit to / only. */
  const [landing, setLanding] = useState(() => !isHubTab(linkedTab));
  const [open, setOpen] = useState(() => isHubTab(linkedTab));
  // Signing in (or up) changes nothing here: the stack stays as it is, so you
  // are still on the step you signed in from, and the action you signed in
  // for (Plant it, Join) runs from the gate.
  const top = stack[stack.length - 1] ?? root;

  // The hub's tab in the address, and nothing else.
  const hubTab = open && top.kind === "hub" ? top.tab : null;
  useEffect(() => {
    if ((params.get("hub") ?? null) === hubTab) return;
    const next = new URLSearchParams(params);
    if (hubTab) next.set("hub", hubTab);
    else next.delete("hub");
    setParams(next, { replace: true });
  }, [hubTab, params, setParams]);

  useEffect(() => {
    if (!open) return;
    if (top.kind === "step" && QUESTION.has(top.step)) track("onboarding_shown", { detail: top.step });
    if (top.kind === "hub") track("hub_opened", { detail: top.tab });
  }, [open, top]);

  const flip = useQuery({
    queryKey: ["web", "flip"],
    queryFn: () => webApi<Flip | null>("/flip"),
    staleTime: 5 * 60_000,
  });
  const flipCard = flip.data?.card ?? null;
  /** The seed in the room behind: the Flip, until someone remixes another here. */
  const [chosenSeed, setChosenSeed] = useState<string | null>(null);
  const stageSeed = chosenSeed ?? flipCard?.id ?? null;

  const stage = useRef<StageHandle | null>(null);
  const onHandle = useCallback((h: StageHandle) => {
    stage.current = h;
  }, []);

  /** Into the room: the modal goes. Nothing plays until the person presses
   * play — the room waits for them. */
  const dropIn = () => {
    if (top.kind === "step" && QUESTION.has(top.step)) track("onboarding_skipped", { detail: top.step });
    setOpen(false);
  };

  /** Home, from anywhere: the studio's logo, the modal's, Back off its first
   * screen. Signed out, the landing; signed in by now (partway through), the
   * feed — the landing is for a first visit. */
  const goHome = () => {
    if (!session) {
      setStack([root]);
      setOpen(false);
      setLanding(true);
      return;
    }
    void (async () => {
      if (stage.current ? await stage.current.ask() : true) navigate("/discover");
    })();
  };

  /** Remix right here: that seed becomes the room behind, and the modal goes. */
  const remixHere = (seedId: string) => {
    track("remix_started", { seedId, detail: "browser" });
    setChosenSeed(seedId);
    setOpen(false);
  };

  return (
    <div className="relative min-h-dvh bg-background">
      {stageSeed ? (
        <LocalRoom
          key={stageSeed}
          seedId={stageSeed}
          stage={{
            onHandle,
            entry: (
              <button
                type="button"
                onClick={goHome}
                aria-label="Seedbed home"
                title="Seedbed home"
                className="flex size-9 shrink-0 items-center justify-center rounded-lg hover:bg-white/5"
              >
                <img src="/seedbed-logo.webp" alt="" className="size-6" />
              </button>
            ),
          }}
        />
      ) : (
        <AmbientGlow />
      )}
      <BeatContext.Provider value={flipCard?.bpm || 120}>
        {landing && (
          <Landing
            onStart={(screens) => {
              track("onboarding_answer", { detail: `landing|${screens[0]?.kind === "hub" ? screens[0].tab : "jam"}` });
              setStack([root, ...screens]);
              setOpen(true);
              setLanding(false);
            }}
            onSkip={() => {
              track("onboarding_skipped", { detail: "landing" });
              setLanding(false);
            }}
          />
        )}
        <HomeModal
          open={open}
          top={top}
          depth={stack.length}
          crumbs={stack.map(crumbOf).filter(Boolean)}
          flip={flipCard}
          onPush={(next) => setStack((s) => [...s, next])}
          onReplace={(next) => setStack((s) => [...s.slice(0, -1), next])}
          onBack={() => (stack.length > 1 ? setStack((s) => s.slice(0, -1)) : goHome())}
          onHome={goHome}
          onClose={dropIn}
          onRemixHere={remixHere}
          onPreview={() => stage.current?.stop()}
          leave={async () => (stage.current ? stage.current.ask() : true)}
        />
      </BeatContext.Provider>
    </div>
  );
}

function HomeModal({
  open,
  top,
  depth,
  crumbs,
  flip,
  onPush,
  onReplace,
  onBack,
  onHome,
  onClose,
  onRemixHere,
  onPreview,
  leave,
}: {
  open: boolean;
  top: Screen;
  depth: number;
  crumbs: string[];
  flip: FeedCard | null;
  onPush: (screen: Screen) => void;
  onReplace: (screen: Screen) => void;
  onBack: () => void;
  onHome: () => void;
  onClose: () => void;
  onRemixHere: (seedId: string) => void;
  onPreview: () => void;
  /** Resolves true when it is fine to leave the page (no unsaved edits, or
   * the person said go). */
  leave: () => Promise<boolean>;
}) {
  const session = useSession();
  const gate = useAuthGate();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialQuery = params.get("q") ?? "";
  const live = useLiveJams().length;
  const jam = useStartJam();
  const step = top.kind === "step" ? top.step : null;

  const answer = (choice: string) => {
    if (step && QUESTION.has(step)) track("onboarding_answer", { detail: `${step}|${choice}` });
  };
  const go = async (to: string, choice?: string) => {
    if (choice) answer(choice);
    if (await leave()) navigate(to);
  };
  const next = (choice: string, to: Screen) => {
    answer(choice);
    onPush(to);
  };
  /** Plant asks for an account first: better at the tap than after the
   * whole upload. Browsing and jamming stay open to anyone. */
  const plant = (thenJam: boolean) =>
    gate.requireSignIn("post_seed", "Make an account to plant a seed", () => onPush({ kind: "upload", thenJam }), "signup");
  const startJam = async (seedId: string) => {
    if (await leave()) jam.start(seedId);
  };
  const jamOnFlip = () => {
    if (!flip) return;
    answer("flip");
    void startJam(flip.id);
  };
  const liveLine = live > 0 ? `${live} live now` : undefined;

  const cardActions = (primary: CardActions["primary"]): CardActions => ({
    primary,
    onJam: (s: HubSeed) => void startJam(s.id),
    onDetails: (s: HubSeed) => void go(seedPath(s.id)),
    onPreview,
  });

  const pickDaw = (seed: { id: string; title: string }, daw: Daw) => {
    track("remix_daw_picked", { seedId: seed.id, detail: daw });
    if (daw === "browser") onRemixHere(seed.id);
    else if (daw === "audiotool") void go(audiotoolHubPath(seed.id));
    else if (daw === "other") onPush({ kind: "other", seed });
    else onPush({ kind: "ableton", seed });
  };

  let body: ReactNode;
  let title: string | null = null;
  /** A phone's version of the title, where the full one would wrap. */
  let shortTitle: string | null = null;
  let footer: ReactNode = null;
  switch (top.kind) {
    case "hub":
      body =
        top.tab === "discover" ? (
          <DiscoverPanel
            initialQuery={initialQuery}
            flip={flip}
            onUpload={() => plant(false)}
            actions={cardActions({ label: "Remix", onClick: (s) => onPush({ kind: "remix", seed: { id: s.id, title: s.title } }) })}
          />
        ) : top.tab === "recent" ? (
          <RecentPanel
            onUpload={() => plant(false)}
            onRemixFlip={flip ? () => onPush({ kind: "remix", seed: { id: flip.id, title: flip.title } }) : undefined}
            onSignIn={() => gate.openSignIn("signin")}
            actions={cardActions({ label: "Open", onClick: (s) => onRemixHere(s.id) })}
          />
        ) : (
          <LivePanel
            onStart={() => onPush({ kind: "step", step: "start_jam" })}
            onJamOnFlip={flip ? jamOnFlip : undefined}
            onJoin={(j) => gate.requireSignIn("join_jam", "Sign in to join this jam", () => void go(roomPath(j.id)))}
          />
        );
      break;
    case "tour":
      body =
        top.tour === "join" ? (
          <LivePanel
            onStart={() => onPush({ kind: "step", step: "start_jam" })}
            onJamOnFlip={flip ? jamOnFlip : undefined}
            onJoin={(j) => gate.requireSignIn("join_jam", "Sign in to join this jam", () => void go(roomPath(j.id)))}
          />
        ) : (
          <DiscoverPanel
            flip={flip}
            actions={
              top.tour === "jam_inspired"
                ? { primary: { label: "Start a jam", onClick: (s) => void startJam(s.id) }, onDetails: (s) => void go(seedPath(s.id)), onPreview }
                : cardActions({ label: "Remix", onClick: (s) => onPush({ kind: "remix", seed: { id: s.id, title: s.title } }) })
            }
          />
        );
      break;
    case "upload":
      title = top.thenJam ? "Plant it, then jam on it" : "Plant a seed";
      body = <UploadFlow initialParentId={null} embedded thenJam={top.thenJam} />;
      break;
    case "remix":
      title = "Remix it where?";
      body = <RemixChooser seedTitle={top.seed.title} onPick={(daw) => pickDaw(top.seed, daw)} />;
      break;
    case "ableton":
      title = "Send it to Live";
      body = <AbletonSend seed={top.seed} />;
      break;
    case "other":
      title = "Not in your DAW yet";
      body = <OtherStep seedId={top.seed.id} embedded />;
      break;
    case "step":
      switch (top.step) {
        case "jam_style":
          // The landing has said hello; this is the first question.
          title = "How do you want to jam?";
          shortTitle = "Jam style?";
          footer = <FooterLink onClick={onClose}>Skip</FooterLink>;
          body = (
            <Tiles>
              <Tile glyph={<SoloGlyph />} title="Jam solo" sub="Just you" onClick={() => next("solo", { kind: "step", step: "solo" })} />
              <Tile glyph={<MultiplayerGlyph />} title="Jam together" sub="Live, with people" onClick={() => next("multiplayer", { kind: "step", step: "multiplayer" })} />
            </Tiles>
          );
          break;
        case "solo":
          title = "Where do you start?";
          footer = <FooterLink onClick={onClose}>Skip</FooterLink>;
          body = (
            <Tiles>
              <Tile glyph={<Mark />} title="Plant a seed" sub="Drop your idea" onClick={() => {
                  answer("plant");
                  plant(false);
                }} />
              <Tile glyph={<RecordGlyph />} title="Get inspired" sub="Find a seed" onClick={() => next("inspired", { kind: "tour", tour: "inspired" })} />
            </Tiles>
          );
          break;
        case "multiplayer":
          title = "Start or join?";
          footer = <FooterLink onClick={onClose}>Skip</FooterLink>;
          body = (
            <Tiles>
              <Tile glyph={<LiveGlyph />} title="Start a jam" sub="Open a room" onClick={() => next("start", { kind: "step", step: "start_jam" })} />
              <Tile glyph={<JoinGlyph />} title="Join a jam" sub={liveLine ?? "See who's on"} live={live > 0} onClick={() => next("join", { kind: "tour", tour: "join" })} />
            </Tiles>
          );
          break;
        case "four":
          title = "Where do you start?";
          footer = <FooterLink onClick={onClose}>Skip</FooterLink>;
          body = (
            <Tiles four>
              <Tile glyph={<Mark />} title="Plant a seed" sub="Drop your idea" onClick={() => {
                  answer("plant");
                  plant(false);
                }} />
              <Tile glyph={<RecordGlyph />} title="Get inspired" sub="Find a seed" onClick={() => next("inspired", { kind: "tour", tour: "inspired" })} />
              <Tile glyph={<LiveGlyph />} title="Start a jam" sub="Open a room" onClick={() => next("start", { kind: "step", step: "start_jam" })} />
              <Tile glyph={<JoinGlyph />} title="Join a jam" sub={liveLine ?? "See who's on"} live={live > 0} onClick={() => next("join", { kind: "tour", tour: "join" })} />
            </Tiles>
          );
          break;
        case "start_jam":
          title = "What are we jamming on?";
          shortTitle = "Jam on what?";
          footer = flip ? <FooterLink onClick={jamOnFlip}>{jam.startingId ? "Opening the room…" : "Jam on this week's Flip"}</FooterLink> : null;
          body = (
            <>
              <Tiles>
                <Tile glyph={<Mark />} title="Plant a seed" sub="Drop your idea" onClick={() => {
                  answer("plant");
                  plant(true);
                }} />
                <Tile glyph={<RecordGlyph />} title="Get inspired" sub="Find a seed" onClick={() => next("inspired", { kind: "tour", tour: "jam_inspired" })} />
              </Tiles>
              {jam.error && <p className="mt-4 text-center text-sm text-removed">{jam.error}</p>}
            </>
          );
          break;
      }
      break;
  }

  const inHub = top.kind === "hub";
  const [drag, setDrag] = useState(0);
  const dragFrom = useRef<number | null>(null);
  const mine = session?.user.handle ? profilePath(session.user.handle) : null;
  const expandTo = inHub ? (top.tab === "discover" ? "/discover" : top.tab === "recent" ? mine : "/jam") : null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[3px]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // The modal itself takes focus, not its first button: a ring on
          // Log in (or the avatar) is the first thing anyone would see.
          tabIndex={-1}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.focus();
          }}
          style={drag > 0 ? { transform: `translateY(${drag}px)`, transition: "none" } : undefined}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[28px] border border-white/10 bg-[#141419]/95 p-5 shadow-[0_30px_80px_rgba(0,0,0,0.7)] outline-none sm:p-8",
            inHub ? "max-w-[620px]" : "max-w-[560px]",
            // On a phone it's a bottom sheet: pinned to the bottom edge, full
            // width, slides up; drag the handle down to close.
            "max-sm:inset-x-0 max-sm:top-auto max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:border-b-0 max-sm:pt-2 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-sm:animate-[sheet-up_280ms_cubic-bezier(0.32,0.72,0,1)] max-sm:transition-transform max-sm:duration-200",
          )}
        >
          {/* Grab handle: drag down to close (phones only). */}
          <div
            className="-mx-5 mb-2 flex h-6 cursor-grab touch-none items-center justify-center sm:hidden"
            onPointerDown={(e) => {
              dragFrom.current = e.clientY;
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => dragFrom.current != null && setDrag(Math.max(0, e.clientY - dragFrom.current))}
            onPointerUp={() => {
              dragFrom.current = null;
              if (drag > 110) onClose();
              setDrag(0);
            }}
          >
            <span className="h-1.5 w-10 rounded-full bg-white/25" />
          </div>
          <div className={cn("flex min-h-10 items-center", inHub ? "mb-5 gap-2 border-b border-white/5 pb-4 sm:gap-3" : "mb-6 gap-3")}>
            {inHub ? (
              <>
                <button type="button" onClick={onHome} aria-label="Seedbed home" title="Seedbed home" className="shrink-0 rounded-lg p-1 hover:bg-white/5">
                  <img src="/seedbed-logo.webp" alt="" className="size-6" />
                </button>
                <DialogPrimitive.Title className="sr-only">{crumbs[crumbs.length - 1]}</DialogPrimitive.Title>
                {/* One row on a phone too: short tab names, and ⤢ moves down into
                    the panel's own top row (HubExpand). */}
                <nav role="tablist" aria-label="Hub" className="flex min-w-0 flex-1 gap-0.5 sm:flex-none sm:gap-1">
                  {HUB_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={top.tab === t.id}
                      onClick={() => onReplace({ kind: "hub", tab: t.id })}
                      className={cn(
                        "h-9 flex-1 shrink-0 rounded-xl px-2 text-sm font-bold transition-colors sm:flex-none sm:px-3",
                        top.tab === t.id ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <span className="sm:hidden">{t.short}</span>
                      <span className="hidden sm:inline">{t.label}</span>
                    </button>
                  ))}
                </nav>
              </>
            ) : depth > 1 ? (
              <>
                <button
                  type="button"
                  onClick={onBack}
                  aria-label="Back"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:bg-white/5"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <nav aria-label="Steps" className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[11px] font-bold uppercase tracking-[0.14em]">
                  {/* Where you are, and on a wider screen the step before it;
                      ‹ walks the rest of the trail. */}
                  {crumbs.slice(-2).map((c, i, shown) => (
                    <span key={`${c}-${i}`} className={cn("shrink-0 items-center gap-1.5", i === shown.length - 1 ? "flex truncate" : "hidden sm:flex")}>
                      {i > 0 && <ChevronRight className="hidden size-3 text-muted-foreground sm:block" />}
                      <span className={i === shown.length - 1 ? "text-foreground" : "text-primary"}>{c}</span>
                    </span>
                  ))}
                </nav>
              </>
            ) : (
              <>
                {/* The first question: Back is the landing. */}
                <button
                  type="button"
                  onClick={onBack}
                  aria-label="Back"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:bg-white/5"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="flex items-center gap-2">
                  <img src="/seedbed-logo.webp" alt="" className="size-6" />
                  <span className="text-lg font-bold tracking-tight">Seedbed</span>
                </span>
              </>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {expandTo && (
                <button
                  type="button"
                  onClick={() => void go(expandTo)}
                  aria-label="Open the full page"
                  title="Open the full page"
                  className="hidden size-10 items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:bg-white/5 sm:flex"
                >
                  <Maximize2 className="size-4" />
                </button>
              )}
              {/* Log in, for anyone with an account; once in, nothing here —
                  you stay on the step you were on. */}
              {!inHub && !session && (
                  <button
                    type="button"
                    onClick={() => gate.openSignIn("signin")}
                    className="rounded-full px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                  >
                    Log in
                  </button>
              )}
              <DialogPrimitive.Close
                aria-label="Close and play"
                className="flex size-10 items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:bg-white/5"
              >
                <X className="size-4" />
              </DialogPrimitive.Close>
            </div>
          </div>
          {title && (
            <DialogPrimitive.Title className={cn("font-black tracking-tight", "mb-6 text-[2rem] leading-tight sm:text-[2.5rem]")}>
              {shortTitle ? (
                <>
                  <span className="sm:hidden">{shortTitle}</span>
                  <span className="hidden sm:inline">{title}</span>
                </>
              ) : (
                title
              )}
            </DialogPrimitive.Title>
          )}
          <HubExpand.Provider value={expandTo ? () => void go(expandTo) : null}>{body}</HubExpand.Provider>
          {footer && <div className="mt-6 flex justify-center">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Ableton: Send to Live (the extension picks it up), or get the extension. */
function AbletonSend({ seed }: { seed: { id: string; title: string } }) {
  const gate = useAuthGate();
  const [state, setState] = useState<"ask" | "sending" | "sent">("ask");
  const send = () =>
    gate.requireSignIn("send_to_live", "Sign in to send this to Live", () => {
      if (!getSession()) return;
      setState("sending");
      webApi(`/seeds/${encodeURIComponent(seed.id)}/send-to-live`, json("POST", {}))
        .then(() => {
          track("sent_to_ableton", { seedId: seed.id });
          track("remix_started", { seedId: seed.id, detail: "ableton" });
        })
        .catch(() => undefined)
        .finally(() => setState("sent"));
    });

  if (state === "sent") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Check className="size-6" />
        </span>
        <p className="font-bold">Waiting for you in Live</p>
        <p className="text-sm text-muted-foreground">Open Seedbed in Live: {seed.title} is in your notifications.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-2.5">
      <button
        type="button"
        onClick={send}
        disabled={state === "sending"}
        className="h-12 rounded-2xl bg-primary text-sm font-bold text-primary-foreground shadow-glow transition-[filter] hover:brightness-110 disabled:opacity-50"
      >
        {state === "sending" ? "Sending…" : "Send to Live"}
      </button>
      <a
        href={remixInAbletonUrl(seed.id)}
        target="_blank"
        rel="noreferrer"
        className="flex h-12 items-center justify-center gap-1.5 rounded-2xl border border-white/10 text-sm font-bold hover:border-white/25"
      >
        Get the extension <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}

/**
 * The landing: what a plain visit to / opens on, before the modal. The room
 * is behind it, blurred. One way in: Let's jam (the questions), or Skip.
 */
function Landing({
  onStart,
  onSkip,
}: {
  onStart: (screens: Screen[]) => void;
  /** Into the room behind, as the modal's Skip. */
  onSkip: () => void;
}) {
  const session = useSession();
  const gate = useAuthGate();
  const bpm = useContext(BeatContext);
  useEffect(() => {
    track("onboarding_shown", { detail: "landing" });
  }, []);
  /** Staggered entrances, in order; nothing moves for reduced motion. */
  const enter = (i: number): CSSProperties => ({ animationDelay: `${120 + i * 110}ms` });
  return (
    <div
      style={{ "--beat": `${(60 / bpm).toFixed(4)}s` } as CSSProperties}
      role="region"
      aria-label="Welcome"
      // Darker on a phone, where the glows fill the whole small screen;
      // a desktop keeps the original wash.
      className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-[#07070a]/95 backdrop-blur-[6px] sm:bg-[#0b0b0e]/90"
    >
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden opacity-50 sm:opacity-100">
        <div className="landing-orb absolute -left-[30%] -top-[25%] size-[720px] rounded-full bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--primary)_16%,transparent),transparent)]" />
        <div className="landing-orb landing-orb-b absolute -bottom-[30%] -right-[30%] size-[680px] rounded-full bg-[radial-gradient(closest-side,rgba(56,90,200,0.22),transparent)]" />
      </div>
      <header className="relative flex h-16 shrink-0 items-center justify-end px-4 sm:px-6">
        {!session && (
          <button
            type="button"
            onClick={() => gate.openSignIn("signin")}
            className="rounded-full px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            Log in
          </button>
        )}
      </header>
      <main className="relative flex flex-1 flex-col items-center justify-center px-6 pb-12 text-center">
        {/* Pops in, then keeps living: a slow float, a sway on the beat, a
            breathing glow — three layers so the transforms don't fight. */}
        <span className="landing-float block">
          <span className="landing-sway block">
            <img src="/seedbed-logo.webp" alt="" className="landing-sprout landing-halo size-20 sm:size-24" />
          </span>
        </span>
        <h1 style={enter(1)} className="landing-in mt-6 text-[2.5rem] font-black leading-[1.05] tracking-tight sm:text-6xl">
          Welcome to Seedbed
        </h1>
        <p style={enter(2)} className="landing-in mt-3 text-base text-muted-foreground sm:text-lg">
          Make music with producers, live.
        </p>
        <div style={enter(3)} className="landing-in mt-9 w-full max-w-sm">
          <button
            type="button"
            onClick={() => onStart([])}
            className="landing-cta flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl bg-primary text-base font-bold text-primary-foreground transition-colors hover:bg-white"
          >
            Let's jam
            <ChevronRight className="size-5" />
          </button>
        </div>
        <button
          type="button"
          style={enter(4)}
          onClick={onSkip}
          className="landing-in mt-5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Skip
        </button>
      </main>
    </div>
  );
}

/** `four`: the A/B's one screen, two rows of shorter tiles. */
function Tiles({ four = false, children }: { four?: boolean; children: ReactNode }) {
  const bpm = useContext(BeatContext);
  return (
    <div
      style={{ "--beat": `${(60 / bpm).toFixed(4)}s` } as CSSProperties}
      className={cn("grid grid-cols-2 gap-3", four && "[&>button]:min-h-36 sm:[&>button]:min-h-40")}
    >
      {children}
    </div>
  );
}

function Tile({ glyph, title, sub, live = false, onClick }: { glyph: ReactNode; title: string; sub: string; live?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tile-beat group flex min-h-44 flex-col items-start rounded-3xl border border-white/10 bg-white/[0.02] p-5 text-left transition-colors hover:border-primary/50 hover:bg-primary/[0.06] sm:min-h-52 sm:p-6"
    >
      <span className="size-11 text-foreground/80 transition-transform group-hover:scale-110 sm:size-12 [&_svg]:size-full">{glyph}</span>
      <span className="mt-auto pt-6 text-lg font-bold sm:text-xl">{title}</span>
      <span className={cn("mt-1 text-sm", live ? "text-primary" : "text-muted-foreground")}>{sub}</span>
    </button>
  );
}

/** The Seedbed mark, for the tiles that are about seeds: plant one, or yours. */
function Mark() {
  return <img src="/seedbed-logo.webp" alt="" className="gl-mark size-full object-contain" />;
}

function FooterLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
      {children}
    </button>
  );
}
